import {
  mapCareerPathResponseToCareerPath,
  type CareerPath,
  type CareerPathResponse,
} from './careerPath';
import type { CareerTrack } from './careerTrack';
import type { Coach } from './coach';
import type { College } from './college';
import { SEARCH_TYPES, type SearchResult } from './contentSearch';
import type { DeepDive } from './deepDive';
import type { EducationalInvestment } from './educationalInvestment';
import type { Employer } from './employer';
import type { ExternalProfile } from './externalProfile';
import type { Industry } from './industry';
import type { Internship } from './internship';
import type { Major } from './major';
import {
  NUGGET_TYPES,
  NUGGET_VARIANTS,
  type ContentCompetency,
  type ContentNugget,
} from './nugget';
import type { PriorityPlaylist } from './playlist';
import type { Scholarship } from './scholarship';
import type { Tool } from './tool';
import type { TrackAssessmentResult, TrackAssessmentUnlock } from './trackAssessment';
import type { BackrsUser } from './user';

export const competencyTransform = (contentCompetency: ContentCompetency): ContentNugget => {
  const competencyAsNugget: ContentNugget = {
    id: contentCompetency.id,
    title: contentCompetency.title,
    description: contentCompetency.description,
    slug: contentCompetency.slug,
    star_rating: 0, // doesn't matter for competencies
    value_rating: 0, // doesn't matter for competencies
    variant: 'competency',
    is_browsable: false, // doesn't matter for competencies
    preview_media: contentCompetency.media_url
      ? { id: contentCompetency.id, media_url: contentCompetency.media_url }
      : undefined,
  };

  return competencyAsNugget;
};

export const scholarshipTransform = (scholarship: Scholarship): ContentNugget => {
  const scholarshipAsNugget: ContentNugget = {
    id: scholarship.id,
    title: scholarship.name,
    description: scholarship.description,
    slug: scholarship.id.toString(),
    star_rating: 0,
    value_rating: 0,
    variant: 'scholarships',
    is_browsable: false,
    scholarship_info: scholarship,
  };

  return scholarshipAsNugget;
};

export const externalProfileTransform = (externalProfile: ExternalProfile): ContentNugget => {
  const externalProfileAsNugget: ContentNugget = {
    id: externalProfile.id,
    title: `Connect with and Review ${externalProfile.first_name} ${externalProfile.last_name}'s Profile`,
    description: externalProfile.description,
    slug: externalProfile.id.toString(),
    star_rating: 0,
    value_rating: 0,
    variant: 'profile-external',
    content_nugget_theme: 'Profile',
    is_browsable: false,
    external_profile_info: externalProfile,
  };

  return externalProfileAsNugget;
};

export const profileTransform = (profile: BackrsUser): ContentNugget => {
  const profileAsNugget: ContentNugget = {
    id: profile.id,
    title: `Connect with and Review ${`${profile.first_name ? profile.first_name : ''} ${profile.last_name ? `${profile.last_name[0]}.` : ''}`}'s Profile`,
    description: profile.bio || '',
    slug: profile.id.toString(),
    star_rating: 0,
    value_rating: 0,
    variant: 'profile-internal',
    profile_info: profile,
    is_browsable: false,
  };

  return profileAsNugget;
};

export const careerPathFromMappedTransform = (careerPath: CareerPath): ContentNugget => ({
  id: careerPath.id,
  title: careerPath.name,
  description: careerPath.description,
  slug: String(careerPath.id),
  star_rating: 0,
  value_rating: 0,
  variant: 'career-path',
  content_nugget_theme: 'Career Path',
  is_browsable: false,
  career_path_info: careerPath,
});

export const careerPathTransform = (career_path: CareerPathResponse): ContentNugget => {
  const careerPathAsNugget: ContentNugget = {
    id: career_path.id,
    title: career_path.name,
    description: career_path.description,
    slug: career_path.id.toString(),
    star_rating: 0,
    value_rating: 0,
    variant: 'career-path',
    content_nugget_theme: 'Career Path',
    is_browsable: false,
    career_path_info: mapCareerPathResponseToCareerPath(career_path),
  };

  return careerPathAsNugget;
};

export const careerTrackTransform = (careerTrack: CareerTrack): ContentNugget => {
  const careerTrackAsNugget: ContentNugget = {
    id: careerTrack.id,
    title: careerTrack.name,
    description: careerTrack.description || '',
    slug: careerTrack.id.toString(),
    star_rating: 0,
    value_rating: 0,
    variant: 'career-track',
    is_browsable: false,
    career_track_info: careerTrack,
  };

  return careerTrackAsNugget;
};

export const collegeTransform = (college: College): ContentNugget => {
  const collegeAsNugget: ContentNugget = {
    id: college.id,
    title: college.school_name,
    description: college.description || '',
    slug: college.id?.toString(),
    star_rating: 0,
    value_rating: 0,
    variant: 'institution',
    external_url: college.url,
    preview_media:
      college.banner_media_id && college.banner_media_url
        ? {
            id: college.banner_media_id,
            media_url: college.banner_media_url,
          }
        : undefined,
    content_nugget_theme: 'College',
    type: 'institution-university',
    is_browsable: false,
    college_info: college,
  };

  return collegeAsNugget;
};

export const employerTransform = (employer: Employer): ContentNugget => {
  const employerAsNugget: ContentNugget = {
    id: employer.id,
    title: employer.name,
    description: employer.the_scoop_about || '',
    slug: employer.slug,
    star_rating: 0,
    value_rating: 0,
    variant: 'employer',
    content_nugget_theme: 'Employer',
    is_browsable: false,
    employer_info: employer,
  };

  return employerAsNugget;
};

export const majorTransform = (major: Major): ContentNugget => {
  const majorAsNugget: ContentNugget = {
    id: major.id,
    title: major.name,
    description: major.description || '',
    slug: major.code || major.id.toString(),
    star_rating: 0,
    value_rating: 0,
    variant: 'major',
    content_nugget_theme: 'Major',
    is_browsable: false,
    major_info: major,
  };

  return majorAsNugget;
};

export const industryTransform = (industry: Industry): ContentNugget => {
  const industryAsNugget: ContentNugget = {
    id: industry.id,
    title: industry.name,
    description: industry.description || '',
    slug: industry.id.toString(),
    star_rating: 0,
    value_rating: 0,
    variant: 'industry',
    content_nugget_theme: 'Industry',
    is_browsable: false,
    industry_info: industry,
  };

  return industryAsNugget;
};

export const internshipTransform = (internship: Internship): ContentNugget => {
  const internshipAsNugget: ContentNugget = {
    id: internship.id,
    title: internship.role_title,
    description: internship.ai_description,
    slug: internship.id.toString(),
    star_rating: 0,
    value_rating: 0,
    variant: 'internship',
    content_nugget_theme: 'Opportunity',
    is_browsable: false,
    internship_info: internship,
  };

  return internshipAsNugget;
};

export const trackAssessmentResultTransform = (
  trackAssessmentResult: TrackAssessmentResult,
): ContentNugget => {
  const trackAssessmentResultAsNugget: ContentNugget = {
    id: trackAssessmentResult.id,
    title: 'Track Assessment Result',
    description: '',
    slug: trackAssessmentResult.id.toString(),
    star_rating: 0,
    value_rating: 0,
    variant: 'track-assessment-result',
    is_browsable: false,
    track_assessment_result_info: trackAssessmentResult,
  };

  return trackAssessmentResultAsNugget;
};

export const trackAssessmentUnlockTransform = (
  trackAssessmentUnlock: TrackAssessmentUnlock,
): ContentNugget => {
  const trackAssessmentUnlockAsNugget: ContentNugget = {
    id: trackAssessmentUnlock.id,
    title: `${trackAssessmentUnlock.category.name} Unlock`,
    description: '',
    slug: trackAssessmentUnlock.id.toString(),
    star_rating: 0,
    value_rating: 0,
    variant: 'track-assessment-unlock',
    is_browsable: false,
    track_assessment_unlock_info: trackAssessmentUnlock,
  };

  return trackAssessmentUnlockAsNugget;
};

export const educationalInvestmentTransform = (
  educationalInvestment: EducationalInvestment,
): ContentNugget => {
  const educationalInvestmentAsNugget: ContentNugget = {
    id: educationalInvestment.id,
    title: `Educational Investment: ${educationalInvestment.name}`,
    description: educationalInvestment.description,
    slug: educationalInvestment.id.toString(),
    star_rating: 0,
    value_rating: 0,
    variant: 'educational-investment',
    is_browsable: false,
    educational_investment_info: educationalInvestment,
  };

  return educationalInvestmentAsNugget;
};

export const toolTransform = (tool: Tool): ContentNugget => {
  const toolAsNugget: ContentNugget = {
    id: tool.id,
    title: tool.title,
    description: tool.description,
    slug: tool.tool_id,
    star_rating: 0,
    value_rating: 0,
    variant: NUGGET_VARIANTS.TOOL,
    is_browsable: false,
    tool_info: tool,
    type: NUGGET_TYPES.TOOL,
  };

  return toolAsNugget;
};

export const coachTransform = (coach: Coach): ContentNugget => {
  const fullName = `${coach.first_name} ${coach.last_name}`;
  const coachAsNugget: ContentNugget = {
    id: coach.id,
    title: fullName,
    description: coach.headline || '',
    slug: fullName.toLowerCase().replace(/\s+/g, '-'),
    star_rating: 0,
    value_rating: 0,
    variant: 'coach',
    content_nugget_theme: 'Coach',
    is_browsable: true,
    coach_info: coach,
  };

  return coachAsNugget;
};

export const deepDiveTransform = (deepDive: DeepDive): ContentNugget => {
  const deepDiveAsNugget: ContentNugget = {
    id: deepDive.id,
    title: deepDive.name,
    description: deepDive.nugget_description || deepDive.description,
    slug: deepDive.name.toLowerCase().replace(/\s+/g, '-'),
    star_rating: 0,
    value_rating: 0,
    variant: 'deep-dive',
    content_nugget_theme: 'Deep Dive',
    is_browsable: true,
    deep_dive_info: deepDive,
  };

  return deepDiveAsNugget;
};

export const blueprintTransform = (responseSetId?: string): ContentNugget => ({
  id: 0,
  slug: 'blueprint',
  title: 'My Blueprint',
  description: '',
  variant: NUGGET_VARIANTS.BLUEPRINT,
  is_browsable: false,
  star_rating: 0,
  value_rating: 0,
  ...(responseSetId && { blueprint_info: { career_user_response_set_id: responseSetId } }),
});

export const playlistTransform = (playlist: PriorityPlaylist): ContentNugget => {
  const playlistAsNugget: ContentNugget = {
    id: playlist.id,
    slug: `playlist-${playlist.id}`,
    title: playlist.title,
    description: playlist.description || '',
    star_rating: 0,
    value_rating: 0,
    variant: NUGGET_VARIANTS.PLAYLIST,
    is_browsable: true,
    playlist_info: playlist,
  };

  return playlistAsNugget;
};

export const searchResultToNugget = (
  result: SearchResult['results'][number]['result'],
): ContentNugget | null => {
  switch (result.type) {
    case SEARCH_TYPES.TOOL:
      return toolTransform(result.tool);

    case SEARCH_TYPES.COMPETENCY:
      return competencyTransform(result.competency);

    case SEARCH_TYPES.SCHOLARSHIP:
      return scholarshipTransform(result.scholarship);

    case SEARCH_TYPES.PROFILE:
      return profileTransform(result.profile);

    case SEARCH_TYPES.CAREER_PATH:
      return careerPathTransform(result.career_path);

    case SEARCH_TYPES.INTERNSHIP:
      return internshipTransform(result.internship);

    case SEARCH_TYPES.EXTERNAL_PROFILE:
      return externalProfileTransform(result.external_profile);

    case SEARCH_TYPES.COLLEGE:
      return collegeTransform(result.college);

    case SEARCH_TYPES.EMPLOYER:
      return employerTransform(result.employer);

    case SEARCH_TYPES.MAJOR:
      return majorTransform(result.major);

    case SEARCH_TYPES.INDUSTRY:
      return industryTransform(result.industry);

    case SEARCH_TYPES.COACH:
      return coachTransform(result.coach);

    case SEARCH_TYPES.DEEP_DIVE:
      return deepDiveTransform(result.deep_dive);

    case SEARCH_TYPES.PLAYLIST:
      return playlistTransform(result.playlist);

    case SEARCH_TYPES.NUGGET:
      return { ...result.nugget };

    default:
      return null;
  }
};

export function transformSearchResultsToNuggets(
  searchResult: SearchResult['results'],
  includeToolsInNuggets = false,
): {
  nuggets: ContentNugget[];
  tools: Tool[];
  playlists: PriorityPlaylist[];
} {
  const tools = (searchResult ?? []).flatMap(({ result }) =>
    result.type === SEARCH_TYPES.TOOL ? [result.tool] : [],
  );

  const playlists = (searchResult ?? []).flatMap(({ result }) =>
    result.type === SEARCH_TYPES.PLAYLIST ? [result.playlist] : [],
  );

  const nuggets = (searchResult ?? []).flatMap(({ result }) => {
    if (result.type === SEARCH_TYPES.TOOL && !includeToolsInNuggets) return [];

    const nugget = searchResultToNugget(result);

    return nugget ? [nugget] : [];
  });

  return { nuggets, tools, playlists };
}
